import React, { useState, useEffect } from 'react';
import { BusinessConsultantChat, BusinessConsultantMessage } from '../../../types/businessConsultant';
import { toast } from 'react-hot-toast';
import { businessConsultantService } from '../../../services/businessConsultantService';

interface HistoryTabProps {
  chats?: BusinessConsultantChat[];
  onNewChat?: () => void;
  onSelectChat?: (chat: BusinessConsultantChat) => void;
}

const HistoryTab: React.FC<HistoryTabProps> = ({ chats = [], onNewChat = () => {}, onSelectChat }) => {
  const [chatPreviews, setChatPreviews] = useState<Record<string, string>>({});

  // Load preview (first user message) for each chat
  useEffect(() => {
    const loadPreviews = async () => {
      const previews: Record<string, string> = {};
      
      for (const chat of chats) {
        try {
          const messages = await businessConsultantService.getMessagesByChatId(chat.id);
          const firstUserMsg = messages.find(m => m.role === 'user');
          
          if (firstUserMsg?.content) {
            const preview = firstUserMsg.content.slice(0, 100);
            previews[chat.id] = preview.length < firstUserMsg.content.length ? `${preview}...` : preview;
          } else {
            previews[chat.id] = 'No messages yet';
          }
        } catch (err) {
          console.error(`Failed to load preview for chat ${chat.id}:`, err);
          previews[chat.id] = 'Unable to load preview';
        }
      }
      
      setChatPreviews(previews);
    };
    
    if (chats.length > 0) {
      loadPreviews();
    }
  }, [chats]);

  if (chats.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">🚀</div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Start Your First Conversation</h3>
        <p className="text-gray-600 mb-6">
          Get instant business advice from your AI consultant
        </p>
        <button
          onClick={onNewChat}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>Start New Chat</span>
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Conversation History</h3>
        <p className="text-sm text-gray-600 mt-1">Browse and continue your past conversations</p>
      </div>

      <div className="space-y-4">
        {chats.map(chat => (
          <div
            key={chat.id}
            className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition hover:shadow-md cursor-pointer"
            onClick={() => {
              if (onSelectChat) {
                onSelectChat(chat);
                toast.success('Opening conversation...');
              }
            }}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-gray-900">{chat.chat_name || 'Untitled Chat'}</h4>
                <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                  {chatPreviews[chat.id] || 'Loading preview...'}
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  {new Date(chat.updated_at || chat.created_at || '').toLocaleString()}
                </p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onSelectChat) {
                    onSelectChat(chat);
                    toast.success('Opening conversation...');
                  } else {
                    toast.error('Chat loading not configured');
                  }
                }}
                className="rounded-md border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-100 flex-shrink-0"
              >
                Open
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HistoryTab;
